import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Common emojis organized by category for quick access
const EMOJI_CATEGORIES = {
  'Récents': ['👋', '🏠', '🍽️', '🚕', '💬', '📚', '🎓', '✨'],
  'Salutations': ['👋', '🤝', '👍', '👏', '🙏', '🫡', '👀', '😊'],
  'Nourriture': ['🍽️', '☕', '🍵', '🥘', '🍞', '🍰', '🥗', '🍜'],
  'Transport': ['🚕', '🚌', '✈️', '🚂', '🚗', '🚶', '🗺️', '📍'],
  'Famille': ['👨‍👩‍👧', '👶', '👴', '👵', '🏠', '❤️', '💑', '👪'],
  'Nature': ['🌞', '🌙', '⭐', '🌧️', '❄️', '🌸', '🌴', '🏖️'],
  'Objets': ['📱', '💻', '📚', '🎒', '🔑', '💳', '🎁', '📦'],
  'Commerce': ['🛒', '💰', '🏪', '🏬', '💵', '🧾', '📊', '💼'],
  'Santé': ['💊', '🏥', '🩺', '🦷', '👁️', '💪', '🏃', '🧘'],
  'Éducation': ['📖', '✏️', '🎓', '📝', '🧮', '📐', '🔬', '🎨'],
  'Culture': ['🇲🇦', '🕌', '🎭', '🎵', '🪘', '🏺', '📿', '🧿'],
  'Expressions': ['😀', '😂', '🤔', '😮', '😢', '😍', '🥰', '🤗'],
};

const EmojiPicker = ({ value, onChange, placeholder = 'Emoji' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Récents');
  const [searchQuery, setSearchQuery] = useState('');
  const pickerRef = useRef(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmojiSelect = (emoji) => {
    onChange(emoji);
    setIsOpen(false);
  };

  // Get all emojis for search
  const allEmojis = Object.values(EMOJI_CATEGORIES).flat();
  const filteredEmojis = searchQuery 
    ? [...new Set(allEmojis)]
    : EMOJI_CATEGORIES[activeCategory] || [];

  return (
    <div className="relative" ref={pickerRef}>
      {/* Input Field */}
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-stone-50 border border-stone-300 rounded-lg px-4 py-3 font-nunito text-2xl text-center"
          maxLength={4}
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="bg-primary hover:bg-primary/90 text-white font-nunito font-bold py-2 px-4 rounded-lg transition-colors"
        >
          {value || '😀'}
        </button>
      </div>

      {/* Emoji Picker Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-stone-200 rounded-xl shadow-xl z-50 overflow-hidden"
          >
            {/* Search */}
            <div className="p-2 border-b border-stone-200">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Rechercher..."
                className="w-full bg-stone-100 rounded-lg px-3 py-2 font-nunito text-sm"
              />
            </div>

            {/* Category Tabs */}
            {!searchQuery && (
              <div className="flex overflow-x-auto gap-1 p-2 border-b border-stone-200 scrollbar-hide">
                {Object.keys(EMOJI_CATEGORIES).map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    className={`px-3 py-1 rounded-full text-xs font-nunito whitespace-nowrap transition-colors ${
                      activeCategory === category
                        ? 'bg-primary text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}

            {/* Emoji Grid */}
            <div className="grid grid-cols-8 gap-1 p-2 max-h-48 overflow-y-auto">
              {filteredEmojis.map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  type="button"
                  onClick={() => handleEmojiSelect(emoji)}
                  className="w-9 h-9 flex items-center justify-center text-xl hover:bg-stone-100 rounded-lg transition-colors"
                >
                  {emoji}
                </button>
              ))}
            </div>

            {/* Quick Morocco Emojis */}
            <div className="p-2 border-t border-stone-200 bg-stone-50">
              <p className="text-xs text-stone-500 font-nunito mb-1">🇲🇦 Maroc:</p>
              <div className="flex gap-1">
                {['🇲🇦', '🕌', '☕', '🫖', '🧿', '🪘', '🏺', '🐪', '🌴', '🏖️'].map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleEmojiSelect(emoji)}
                    className="w-7 h-7 flex items-center justify-center text-lg hover:bg-stone-200 rounded transition-colors"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmojiPicker;
